import { ROUTES_NAMES } from "../../components/routes";
import type {
  ExplorerLivestreamCompetingCategory,
  ExplorerLivestreamCompetingChannel,
  ExplorerLivestreamCompetingWindows,
} from "../../services/api";

export type CompetingStreamAirtime = {
  actualStartTime?: string;
  actualEndTime?: string;
  youtubeDuration?: number;
  totalDuration?: number;
  releaseTime?: string;
};

function toMs(value?: string): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function durationSeconds(item: CompetingStreamAirtime): number {
  if (item.youtubeDuration != null && item.youtubeDuration > 0) {
    return item.youtubeDuration;
  }
  if (item.totalDuration != null && item.totalDuration > 0) {
    return item.totalDuration;
  }
  return 0;
}

export function streamAirWindow(
  item: CompetingStreamAirtime,
  nowMs: number = Date.now(),
): { start: number; end: number } | null {
  const start = toMs(item.actualStartTime) ?? toMs(item.releaseTime);
  if (start == null) return null;
  const ended = toMs(item.actualEndTime);
  if (ended != null) return { start, end: ended };
  const seconds = durationSeconds(item);
  if (seconds > 0) return { start, end: start + seconds * 1000 };
  return { start, end: nowMs };
}

export function windowsOverlap(
  start: number,
  end: number,
  otherStart: number,
  otherEnd: number,
): boolean {
  return start < otherEnd && end > otherStart;
}

export function overlappingCompetingChannels(
  stream: CompetingStreamAirtime,
  competing: ExplorerLivestreamCompetingWindows | undefined,
  nowMs: number = Date.now(),
): ExplorerLivestreamCompetingChannel[] {
  if (!competing) return [];
  const air = streamAirWindow(stream, nowMs);
  if (!air) return [];

  const seen = new Set<string>();
  const channels: ExplorerLivestreamCompetingChannel[] = [];
  for (const window of competing.windows) {
    const windowStart = toMs(window.start);
    const windowEnd = toMs(window.end);
    if (windowStart == null || windowEnd == null) continue;
    if (!windowsOverlap(air.start, air.end, windowStart, windowEnd)) continue;
    if (seen.has(window.channelName)) continue;
    seen.add(window.channelName);
    const meta = competing.channels[window.channelName] ?? {
      name: window.channelName,
    };
    if (competing.category && meta.category !== competing.category) continue;
    channels.push(meta);
  }
  return channels;
}

export function competitionPageHref(
  stream: CompetingStreamAirtime,
  category: ExplorerLivestreamCompetingCategory = "tv",
  nowMs: number = Date.now(),
): string | undefined {
  const air = streamAirWindow(stream, nowMs);
  if (!air) return undefined;
  const params = new URLSearchParams({
    start: new Date(air.start).toISOString(),
    end: new Date(air.end).toISOString(),
    category,
  });
  return `${ROUTES_NAMES.EXPLORER_LIVESTREAM_COMPETITION}?${params.toString()}`;
}
